import React, { useEffect, useRef } from "react";
import { useForm, ValidationError } from "@formspree/react";
import { META_PIXEL_ID } from "../data/metaPixel";

/* global fbq */

const EnquireModal = ({ isOpen, onClose, paintingTitle, price }) => {
  const [state, handleSubmit] = useForm("xrevlqan");
  const firstFieldRef = useRef(null);
  const overlayRef = useRef(null);
  // Captured at submit time (not from state) because the form fields are
  // uncontrolled and the form unmounts once state.succeeded flips true —
  // by then the inputs are gone, so we can't read them in the effect below.
  const emailRef = useRef("");
  const nameRef = useRef("");

  // Compose the "regarding" line shown in the form header
  const regarding = paintingTitle
    ? price
      ? `${paintingTitle} — $${price.toLocaleString()} AUD`
      : paintingTitle
    : "Painting enquiry";

  // Scroll lock + auto-focus
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      setTimeout(() => firstFieldRef.current?.focus(), 50);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  // Fire Facebook pixel on successful send, with Advanced Matching
  // (email/name) so Meta can link the visit to a real person instead of
  // relying on IP + user agent alone.
  useEffect(() => {
    if (state.succeeded && typeof fbq !== "undefined") {
      const matchData = {};
      if (emailRef.current) matchData.em = emailRef.current.trim().toLowerCase();
      if (nameRef.current) {
        const [firstName, ...rest] = nameRef.current.trim().split(/\s+/);
        if (firstName) matchData.fn = firstName.toLowerCase();
        if (rest.length) matchData.ln = rest.join(" ").toLowerCase();
      }
      if (Object.keys(matchData).length) {
        fbq("init", META_PIXEL_ID, matchData);
      }
      fbq("track", "Contact", {
        ...(paintingTitle && { content_name: paintingTitle }),
      });
    }
  }, [state.succeeded, paintingTitle]);

  // Capture name/email before handing off to Formspree's submit handler —
  // see emailRef/nameRef comment above for why this can't happen later.
  const onSubmit = (e) => {
    const formData = new FormData(e.target);
    emailRef.current = formData.get("email") || "";
    nameRef.current = formData.get("name") || "";
    handleSubmit(e);
  };

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="emOverlay"
      ref={overlayRef}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Enquire about this painting"
    >
      <div className="emPanel">
        <button className="emClose" onClick={onClose} aria-label="Close">
          &#x2715;
        </button>

        {state.succeeded ? (
          <div className="emSuccess">
            <p className="emSuccessHead">Thank you for your enquiry.</p>
            <p className="emSuccessBody">
              I'll be in touch within 1–2 business days.
            </p>
            <button className="emSubmit" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <>
            <p className="emEyebrow">Enquire about this painting</p>
            <p className="emRegarding">{regarding}</p>

            <form onSubmit={onSubmit} noValidate>
              {/* Hidden fields so Formspree email includes painting details */}
              <input type="hidden" name="painting" value={regarding} />
              <input
                type="hidden"
                name="_subject"
                value={`Enquiry — ${paintingTitle || "Painting"}`}
              />

              <div className="emField">
                <label className="emLabel" htmlFor="em-name">
                  Name
                </label>
                <input
                  id="em-name"
                  className="emInput"
                  type="text"
                  name="name"
                  required
                  autoComplete="name"
                  ref={firstFieldRef}
                />
                <ValidationError
                  field="name"
                  prefix="Name"
                  errors={state.errors}
                  className="emError"
                />
              </div>

              <div className="emField">
                <label className="emLabel" htmlFor="em-email">
                  Email
                </label>
                <input
                  id="em-email"
                  className="emInput"
                  type="email"
                  name="email"
                  required
                  autoComplete="email"
                />
                <ValidationError
                  field="email"
                  prefix="Email"
                  errors={state.errors}
                  className="emError"
                />
              </div>

              <div className="emField">
                <label className="emLabel" htmlFor="em-message">
                  Message
                </label>
                <textarea
                  id="em-message"
                  className="emInput emTextarea"
                  name="message"
                  rows={4}
                  placeholder="Any questions about the work, shipping, or pricing…"
                />
                <ValidationError
                  field="message"
                  prefix="Message"
                  errors={state.errors}
                  className="emError"
                />
              </div>

              {/* General (non-field) errors */}
              {state.errors && state.errors.length > 0 &&
                !state.errors.some((e) => e.field) && (
                  <p className="emError">
                    Something went wrong — please try again or email{" "}
                    <a href="mailto:tyrone@tyronelayne.com">
                      tyrone@tyronelayne.com
                    </a>{" "}
                    directly.
                  </p>
                )}

              <button
                className="emSubmit"
                type="submit"
                disabled={state.submitting}
              >
                {state.submitting ? "Sending…" : "Send enquiry"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default EnquireModal;
